/**
 * Sector terminology, keyed by the active org's `flavour` (see Org.Flavour on
 * the backend: school | university | estate | business | home). The school
 * wording is the canonical fallback: a term missing for a flavour resolves to the
 * school term, and an unknown flavour resolves entirely to school terms, so a
 * lookup can never render an empty label.
 *
 * `estate` and `business` are deliberately distinct rather than sharing a column:
 * a landlord lets Properties and records voids between tenancies, while a pub or
 * retail group operates Sites and closes them. Using the landlord's words for a
 * trading business was the gap this vocabulary was added to close.
 *
 * Pure module by design — `getTerm` is usable anywhere (including for an org
 * other than the active one, e.g. the site-admin feature-flag editor). Inside
 * components rendering for the active org, prefer the `useTerminology` hook.
 */

export const SCHOOL_FLAVOUR = 'school'

export const ESTATE_FLAVOUR = 'estate'

export const BUSINESS_FLAVOUR = 'business'

export const FLAVOURS = ['school', 'university', 'estate', 'business', 'home']

export const TERMS = {
    house: {
        school: 'House',
        university: 'Hall',
        estate: 'Property',
        business: 'Site',
        home: 'House'
    },
    houses: {
        school: 'Houses',
        university: 'Halls',
        estate: 'Properties',
        business: 'Sites',
        home: 'Houses'
    },
    pupil: {
        school: 'pupil',
        university: 'student',
        estate: 'occupant',
        business: 'occupant',
        home: 'occupant'
    },
    pupils: {
        school: 'pupils',
        university: 'students',
        estate: 'occupants',
        business: 'occupants',
        home: 'occupants'
    },
    holidays: {
        school: 'Holidays',
        university: 'Vacations',
        estate: 'Unoccupied periods',
        business: 'Closures',
        home: 'Away periods'
    },
    // The single-Location target of an alert rule, budget, bill statement or digest
    // subscription (PropertyScopeSelector's label, list target cells, digest titles).
    // University is "Building", not "Hall": its metered Locations are academic buildings
    // as often as halls. Defining it for a flavour does not make the scope available
    // there — that is LOCATION_SCOPE_FLAVOURS in lib/locationScope.js.
    scopeLocation: {
        school: 'Building',
        university: 'Building',
        estate: 'Property',
        business: 'Site',
        home: 'Property'
    },
    // pupil-groups-houses: the admin page that manages houses (and other pupil groups), and
    // the engagement league on the Houses page. Estate, business and home orgs have no pupil
    // groups; their entries exist so a lookup never falls back to school wording.
    groups: {
        school: 'Houses & groups',
        university: 'Halls & groups',
        estate: 'Properties & groups',
        business: 'Sites & groups',
        home: 'Houses & groups'
    },
    houseLeague: {
        school: 'House league',
        university: 'Hall league',
        estate: 'Property league',
        business: 'Site league',
        home: 'House league'
    },
    // The location member role's label (location-scoped-member-role): role picker,
    // add-person dialog, pending invitations, member table, alert/digest audiences and the
    // empty state. Mirrored word for word in backend core/terminology.py.
    locationMemberRole: {
        school: 'Housemaster',
        university: 'Hall warden',
        estate: 'Tenant',
        business: 'Site manager',
        home: 'Household member'
    },
    locationMemberRoles: {
        school: 'Housemasters',
        university: 'Hall wardens',
        estate: 'Tenants',
        business: 'Site managers',
        home: 'Household members'
    },
    housesSummaryTitle: {
        school: 'Houses Summary',
        university: 'Halls Summary',
        estate: 'Properties Summary',
        business: 'Sites Summary',
        home: 'House Summary'
    },
    // The intra-org property league page (Power section, estate and business only by
    // route gating; defined for every flavour so the dictionary stays complete).
    housesLeagueTitle: {
        school: 'Houses league',
        university: 'Halls league',
        estate: 'Properties league',
        business: 'Sites league',
        home: 'House league'
    },
    houseDetailsTitle: {
        school: 'House Details',
        university: 'Hall Details',
        estate: 'Property Details',
        business: 'Site Details',
        home: 'House Details'
    },
    holidayPeriodsHeading: {
        school: 'Holiday Periods',
        university: 'Vacation periods',
        estate: 'Unoccupied periods',
        business: 'Closed periods',
        home: 'Away periods'
    },
    // The explainer above the holiday charts. A full sentence per flavour, like the taglines
    // below: it used to say "holiday" to a pub group and a landlord alike.
    holidayExpectation: {
        school: 'Each chart shows the holiday’s actual consumption as bars, with a baseline line marking usual consumption. Because the school is largely empty over a holiday, expect the bars to sit well below the line: a large gap means good shutdown savings, while bars near or above it suggest equipment left running.',
        university:
            'Each chart shows the vacation’s actual consumption as bars, with a baseline line marking usual consumption. Because much of the campus is empty over a vacation, expect the bars to sit below the line: a large gap means good shutdown savings, while bars near or above it suggest equipment left running.',
        estate: 'Each chart shows the unoccupied period’s actual consumption as bars, with a baseline line marking usual consumption. Because the property is empty, expect the bars to sit well below the line: a large gap means good shutdown savings, while bars near or above it suggest equipment left running.',
        business:
            'Each chart shows the closure’s actual consumption as bars, with a baseline line marking usual consumption. Because the site is shut, expect the bars to sit well below the line: a large gap means good shutdown savings, while bars near or above it suggest equipment left running.',
        home: 'Each chart shows the away period’s actual consumption as bars, with a baseline line marking usual consumption. Because nobody is home, expect the bars to sit well below the line: a large gap means appliances were switched off, while bars near or above it suggest something left running.'
    },
    // Homepage tile blurbs. Full sentences per flavour rather than a noun swap: the holidays
    // one genuinely differs in shape (a school compares against term time, a business against
    // trading days, a household against time at home), and lowercasing a term to splice into a
    // sentence produces grammar that drifts as terms are added.
    housesTagline: {
        school: 'How is my house performing?',
        university: 'How is my hall performing?',
        estate: 'How is each property performing?',
        business: 'How is each site performing?',
        home: 'How is my house performing?'
    },
    houseDetailsTagline: {
        school: "Deep-dive into a single house's data",
        university: "Deep-dive into a single hall's data",
        estate: "Deep-dive into a single property's data",
        business: "Deep-dive into a single site's data",
        home: "Deep-dive into a single house's data"
    },
    holidaysTagline: {
        school: 'Compare holiday usage against term time to prove we use less',
        university:
            'Compare vacation usage against term time to prove we use less',
        business:
            'See what your sites draw when closed, against normal trading days',
        estate: 'See what each property draws while unoccupied, against normal weeks',
        home: 'See what the house draws while you are away, against normal weeks'
    },
    classProjectsTitle: { school: 'Class Projects' },
    energyQuizTitle: { school: 'Energy Quiz' },
    targetsTitle: { school: 'Reduction Targets' },
    activitiesTitle: { school: 'Activities' },
    holidaysTitle: {
        school: 'Holidays',
        university: 'Vacations',
        estate: 'Unoccupied periods',
        business: 'Closures',
        home: 'Away periods'
    },
    // PowerHolidays page furniture. Split into narrow keys rather than one composed sentence
    // so each control keeps its own wording — sector-neutral-surfaces 3.4/3.5: every path
    // through the page, including loading and error states, must speak one vocabulary.
    holidaysPageSubtitleByHoliday: {
        school: 'View meter readings during holiday periods to analyze power consumption reductions',
        university:
            'View meter readings during vacation periods to analyze power consumption reductions',
        estate: 'View meter readings during unoccupied periods to analyze power consumption reductions',
        business:
            'View meter readings during closed periods to analyze power consumption reductions',
        home: 'View meter readings during away periods to analyze power consumption reductions'
    },
    holidaysPageSubtitleByMeter: {
        school: 'View every holiday period for a selected meter to analyze power consumption reductions',
        university:
            'View every vacation period for a selected meter to analyze power consumption reductions',
        estate: 'View every unoccupied period for a selected meter to analyze power consumption reductions',
        business:
            'View every closed period for a selected meter to analyze power consumption reductions',
        home: 'View every away period for a selected meter to analyze power consumption reductions'
    },
    holidaysPageSubtitleTrend: {
        school: 'Compare a single metric across every holiday period to spot trends over time',
        university:
            'Compare a single metric across every vacation period to spot trends over time',
        estate: 'Compare a single metric across every unoccupied period to spot trends over time',
        business:
            'Compare a single metric across every closed period to spot trends over time',
        home: 'Compare a single metric across every away period to spot trends over time'
    },
    holidaysByHolidayToggleLabel: {
        school: 'By Holiday',
        university: 'By Vacation',
        estate: 'By Unoccupied Period',
        business: 'By Closure',
        home: 'By Away Period'
    },
    holidaysByHolidayToggleAriaLabel: {
        school: 'show all meters for one holiday',
        university: 'show all meters for one vacation',
        estate: 'show all meters for one unoccupied period',
        business: 'show all meters for one closure',
        home: 'show all meters for one away period'
    },
    holidaysByMeterToggleAriaLabel: {
        school: 'show all holidays for one meter',
        university: 'show all vacations for one meter',
        estate: 'show all unoccupied periods for one meter',
        business: 'show all closures for one meter',
        home: 'show all away periods for one meter'
    },
    holidaysTrendToggleAriaLabel: {
        school: 'show trend across holidays',
        university: 'show trend across vacations',
        estate: 'show trend across unoccupied periods',
        business: 'show trend across closures',
        home: 'show trend across away periods'
    },
    holidaysDownloadTooltip: {
        school: 'Download holiday summaries',
        university: 'Download vacation summaries',
        estate: 'Download unoccupied-period summaries',
        business: 'Download closure summaries',
        home: 'Download away-period summaries'
    },
    holidaysDownloadLoadingTooltip: {
        school: 'Loading holiday summaries…',
        university: 'Loading vacation summaries…',
        estate: 'Loading unoccupied-period summaries…',
        business: 'Loading closure summaries…',
        home: 'Loading away-period summaries…'
    },
    holidaysNoSummariesTooltip: {
        school: 'No holiday summaries to download',
        university: 'No vacation summaries to download',
        estate: 'No unoccupied-period summaries to download',
        business: 'No closure summaries to download',
        home: 'No away-period summaries to download'
    },
    holidaysPngUnsupportedTooltip: {
        school: "PNG export isn't available for holiday summaries",
        university: "PNG export isn't available for vacation summaries",
        estate: "PNG export isn't available for unoccupied-period summaries",
        business: "PNG export isn't available for closure summaries",
        home: "PNG export isn't available for away-period summaries"
    },
    holidaysEmptyStateMessage: {
        school: 'No holiday periods found. Contact your administrator to add holiday periods.',
        university:
            'No vacation periods found. Contact your administrator to add vacation periods.',
        estate: 'No unoccupied periods found. Contact your administrator to add unoccupied periods.',
        business:
            'No closed periods found. Contact your administrator to add closed periods.',
        home: 'No away periods found. Contact your administrator to add away periods.'
    },
    // Admin Holidays page (sector-neutral-surfaces 3.1). The page this org's onboarding step
    // (setupLabels.js `holidays_or_term_dates`) deep-links to, so its wording must match the
    // step that sent the admin here.
    adminHolidaysDescription: {
        school: 'Maintain holiday periods used by power reporting.',
        university: 'Maintain vacation periods used by power reporting.',
        estate: 'Maintain unoccupied periods used by power reporting.',
        business: 'Maintain closure periods used by power reporting.',
        home: 'Maintain away periods used by power reporting.'
    },
    adminHolidaysLoadErrorAlert: {
        school: 'Unable to load holiday data.',
        university: 'Unable to load vacation data.',
        estate: 'Unable to load unoccupied-period data.',
        business: 'Unable to load closure data.',
        home: 'Unable to load away-period data.'
    },
    adminHolidayDeletedToast: {
        school: 'Holiday deleted.',
        university: 'Vacation deleted.',
        estate: 'Unoccupied period deleted.',
        business: 'Closure deleted.',
        home: 'Away period deleted.'
    },
    adminHolidayDeleteErrorToast: {
        school: 'Unable to delete holiday.',
        university: 'Unable to delete vacation.',
        estate: 'Unable to delete unoccupied period.',
        business: 'Unable to delete closure.',
        home: 'Unable to delete away period.'
    },
    // Closing line on a generated export README (composeReadme.js). Unconditional on every
    // export regardless of kind, so a business admin exporting closure data was told to "tell
    // your teacher" — sector-neutral-surfaces 4.1.
    holidaysLoadErrorPrefix: {
        school: 'Failed to load holidays',
        university: 'Failed to load vacations',
        estate: 'Failed to load unoccupied periods',
        business: 'Failed to load closures',
        home: 'Failed to load away periods'
    },
    // Singular, Title Case noun for one holiday/vacation/closure — sector-neutral-surfaces
    // 3.6. Used to compose labels inside the PowerHolidays view components ("Select
    // Closure", "previous closure"). Deliberately distinct from `holidaysByHolidayToggleLabel`
    // ("By Closure"), which is a full authored phrase rather than a bare noun.
    holidaySingular: {
        school: 'Holiday',
        university: 'Vacation',
        estate: 'Unoccupied Period',
        business: 'Closure',
        home: 'Away Period'
    },
    // HolidayComparisonTable's caption footnote (sector-neutral-surfaces 3.6). Authored per
    // flavour rather than a `holidaySingular` splice: "both unoccupied-period and baseline
    // values" needs the hyphenated compound a bare noun swap would not produce.
    holidayComparisonCaption: {
        school: 'Figures compare days where both holiday and baseline values exist. A negative Δ (green) means the holiday used less than the baseline. Holiday Cost and Addressable £ are ex-VAT and come from the metered tariff (hover a cost for its source); CO₂ is estimated from grid carbon intensity. Rows flagged as anomalies are highlighted; rows without enough data are marked accordingly.',
        university:
            'Figures compare days where both vacation and baseline values exist. A negative Δ (green) means the vacation used less than the baseline. Vacation Cost and Addressable £ are ex-VAT and come from the metered tariff (hover a cost for its source); CO₂ is estimated from grid carbon intensity. Rows flagged as anomalies are highlighted; rows without enough data are marked accordingly.',
        estate: 'Figures compare days where both unoccupied-period and baseline values exist. A negative Δ (green) means the unoccupied period used less than the baseline. Unoccupied Period Cost and Addressable £ are ex-VAT and come from the metered tariff (hover a cost for its source); CO₂ is estimated from grid carbon intensity. Rows flagged as anomalies are highlighted; rows without enough data are marked accordingly.',
        business:
            'Figures compare days where both closure and baseline values exist. A negative Δ (green) means the closure used less than the baseline. Closure Cost and Addressable £ are ex-VAT and come from the metered tariff (hover a cost for its source); CO₂ is estimated from grid carbon intensity. Rows flagged as anomalies are highlighted; rows without enough data are marked accordingly.',
        home: 'Figures compare days where both away-period and baseline values exist. A negative Δ (green) means the away period used less than the baseline. Away Period Cost and Addressable £ are ex-VAT and come from the metered tariff (hover a cost for its source); CO₂ is estimated from grid carbon intensity. Rows flagged as anomalies are highlighted; rows without enough data are marked accordingly.'
    },
    readmeClosingNote: {
        school: 'Made something cool with this? Tell your teacher!',
        university:
            'Questions about this data? Contact your energy or estates team.',
        estate: 'Questions about this data? Contact your property manager.',
        business: 'Questions about this data? Contact your site manager.',
        home: 'Questions about this data? Get in touch with support.'
    },
    tenant: { school: 'tenant', estate: 'tenant', home: 'resident' },
    tenancy: { school: 'tenancy', estate: 'tenancy' },
    // Plural heading (the Property Hub tab), so the label is not built by concatenation.
    tenancies: { school: 'Tenancies', estate: 'Tenancies' },
    // Estate keeps the tenancy sense; a business closes a site rather than voiding it, which
    // is why the backend classifies a business closed day as `closed` (period_calendar.py).
    void: { school: 'void', estate: 'void', business: 'closure' },
    // The org itself, for copy like "your {orgNoun}" / "the {orgNoun}".
    orgNoun: {
        school: 'school',
        university: 'university',
        estate: 'estate',
        business: 'business',
        home: 'home'
    },
    // Overview page subtitle (reward-and-voice, home-flavour-fit). "Our" reads fine for an
    // institution reading about itself; only home — a single household — gets different
    // wording. School wording is unchanged by construction.
    overviewSubtitle: {
        school: 'Insights into our power usage',
        university: 'Insights into our power usage',
        estate: 'Insights into our power usage',
        business: 'Insights into our power usage',
        home: "Insights into your home's power usage"
    },
    // absence-and-loading-states 2.2: the shared EmptyState component's
    // `roleName` fallback for "who can act instead" when the current reader
    // cannot. Uniform across flavours today (there is no flavour-specific
    // admin title yet), but routed through the terminology layer rather than
    // a hardcoded literal so a future flavour-specific admin title (or a
    // household's own wording) has one place to change.
    orgAdminRoleName: {
        school: 'your organisation admin',
        university: 'your organisation admin',
        estate: 'your organisation admin',
        business: 'your organisation admin',
        home: 'your organisation admin'
    },
    // Insight-card explanatory copy (sector-neutral-surfaces #2). Per design D5 the defect on
    // these cards was inverted CLAIMS ("weekend use collapses to baseload"), not vocabulary —
    // substituting a noun into the school sentence produces grammatical text that is still
    // wrong for a weekend-trading business. Each flavour's paragraph is authored for what is
    // actually true of that org's occupancy or trading pattern, not templated from school's.
    insightOutOfHoursWindowLabel: {
        school: 'the school day, weekends, and holidays',
        university: 'the teaching timetable, evenings and vacations',
        estate: 'the hours you have set as normal occupancy for each property',
        business: 'your trading hours',
        home: 'the hours you are normally at home'
    },
    insightOutOfHoursSummary: {
        school: 'Consumption during hours your buildings should be empty: evenings, weekends and holidays. This is the most common finding in a school energy audit, and usually the cheapest to fix.',
        university:
            'Consumption outside your teaching timetable (evenings, nights and vacations), when lecture theatres, offices and labs should be quiet. This is one of the most common findings in a university energy review, and usually the cheapest to fix.',
        estate: 'Consumption outside the hours each property is normally occupied. This is one of the most common findings in a property energy review, and usually the cheapest to fix.',
        business:
            'Consumption outside your trading hours, whatever they are: for many businesses that is not evenings or weekends. This is one of the most common findings in a site energy review, and usually the cheapest to fix.',
        home: 'Consumption while nobody is normally home. This is one of the most common findings in a home energy check, and usually the cheapest to fix.'
    },
    insightTimeOfUseWindowLabel: {
        school: 'the school day',
        university: 'the teaching day',
        estate: 'standard daytime hours',
        business: 'standard daytime hours',
        home: 'standard daytime hours'
    },
    insightTimeOfUseExplainer1: {
        school: 'Energy is split by clock time into the occupied school day and everything outside it. The out-of-hours portion is the classic place to find waste (plant, lighting and IT left running when the buildings are quiet), and it often sits on a different (cheaper) tariff band, so shifting flexible load into it can also cut cost.',
        university:
            'Energy is split by clock time into the teaching day and everything outside it. On the teaching and admin estate, the out-of-hours portion is the classic place to find waste (labs, lighting and IT left running when buildings are quiet), though halls of residence are expected to draw power outside these hours too. It often sits on a different (cheaper) tariff band, so shifting flexible load into it can also cut cost.',
        estate: 'Energy is split by clock time into standard daytime hours and everything outside it. For a residential estate, a large share outside daytime hours is expected (occupants are typically out during the day and home in the evening), so this split is more useful for spotting shared-area waste (corridors, plant, communal lighting) than for judging occupant use.',
        business:
            'Energy is split by clock time into standard daytime hours and everything outside it. For a site that trades evenings or weekends, a large out-of-hours share here simply reflects your trading pattern; the split is more useful for spotting waste during the hours you are actually closed, and for checking whether that load sits on a cheaper tariff band worth shifting flexible use into.',
        home: 'Energy is split by clock time into standard daytime hours and everything outside it. For most households, evenings and nights, when people are home, make up the larger share, so this split is more useful for spotting appliances left running during the day than for judging normal evening use.'
    },
    insightTimeOfUseExplainer2: {
        school: 'Why it matters here: a boarding school is genuinely busy in the evenings and at weekends, so a large out-of-hours share is expected. The value is watching that share over time and sense-checking it against when people are actually in the building.',
        university:
            'Why it matters here: halls of residence are genuinely busy in the evenings and at weekends, even while teaching buildings empty out, so a large out-of-hours share is expected. The value is watching that share over time and sense-checking it against your term calendar and hall occupancy.',
        estate: 'Why it matters here: occupants are usually out during the day and home in the evening, so a large out-of-hours share is expected and healthy. The value is watching for shared-area or void consumption that does not follow that pattern.',
        business:
            'Why it matters here: a site that trades evenings or weekends is genuinely busy outside standard daytime hours, so a large out-of-hours share is expected. The value is watching that share over time and sense-checking it against your actual trading hours.',
        home: 'Why it matters here: most households are busier in the evenings and at weekends than during the day, so a large out-of-hours share is expected. The value is watching that share over time and sense-checking it against when people are actually home.'
    },
    insightWeekdayWeekendExplainer1: {
        school: "In a typical day school weekend use collapses to baseload. A boarding school doesn't empty out (students are resident), so weekend consumption staying close to weekday levels is expected and healthy.",
        university:
            'For most universities, weekend use drops well below weekday levels as teaching and admin buildings empty out, though halls of residence keep drawing power throughout, so an all-university drop that is large but not total, with a floor set by the halls, is expected and healthy.',
        estate: 'For a residential estate, weekend use staying close to weekday levels, or even higher, is expected: occupants who are out at work on weekdays are typically home over the weekend.',
        business:
            'For a site that trades on weekends (a pub, retail unit or venue), weekend use as high as, or higher than, weekday use is expected and healthy: that is when you are busiest, not when use should collapse to baseload.',
        home: 'Weekend use close to or above weekday levels is expected and healthy for a home: you are typically in more over the weekend than during a working week.'
    },
    insightWeekdayWeekendExplainer2: {
        school: 'Why it matters: the weekend-to-weekday ratio is a quick occupancy sense-check. A weekend figure that creeps unusually high (or a weekday that dips toward weekend levels in term time) is worth a look.',
        university:
            'Why it matters: the weekend-to-weekday ratio is a quick occupancy sense-check. A weekday that dips toward weekend levels in term time, or a weekend that creeps unusually low with halls still occupied, is worth a look.',
        estate: 'Why it matters: the weekend-to-weekday ratio is a quick occupancy sense-check. A weekend figure that is unusually low for a fully-let estate, or a weekday that is unusually high, is worth a look.',
        business:
            'Why it matters: the weekend-to-weekday ratio is a quick trading-pattern sense-check. A weekend figure that is lower than your trading hours would suggest, or a weekday figure that is higher than a closed site should be, is worth a look.',
        home: 'Why it matters: the weekend-to-weekday ratio is a quick occupancy sense-check. A weekday figure that stays high while the house should be empty, or a weekend that is unexpectedly low, is worth a look.'
    },
    insightHeatmapExplainer2: {
        school: "Why it matters here: in a boarding school there are students in the building on evenings and weekends, so you would not expect those to go dark. The value is in checking that the overnight and holiday troughs fall as low as they should, and in spotting plant left running outside the times it's actually needed.",
        university:
            'Why it matters here: halls of residence keep the building lit through evenings and weekends even as teaching spaces go quiet, so do not expect those bands to go fully dark. The value is in checking that overnight and vacation troughs fall as low as they should, and in spotting plant left running outside the times it is actually needed.',
        estate: "Why it matters here: occupied properties draw power around the clock, so do not expect full darkness at any time of day. The value is in checking that the troughs during a property's unoccupied periods fall as low as they should, and in spotting communal plant left running outside the times it is actually needed.",
        business:
            'Why it matters here: a site that trades evenings or weekends will show bright cells exactly then. That is trade, not waste. The value is in checking that the troughs during your actual closed hours fall as low as they should, and in spotting plant left running outside the times it is actually needed.',
        home: 'Why it matters here: a home is naturally brighter in the evenings and at weekends, when people are in. The value is in checking that the troughs during the working day and while you are away fall as low as they should, and in spotting appliances left running outside the times they are actually needed.'
    },
    insightEnergySignatureExplainer2: {
        school: 'Why it matters here: as a boarding school the site is occupied around the clock, so that baseline is genuinely large. The signature is the clean way to separate that constant occupancy load from weather-driven heating. Dots well above the line are days worth investigating; a baseline that drifts up over time is creep in the always-on load.',
        university:
            'Why it matters here: with halls of residence occupied around the clock even as teaching buildings empty out overnight, the baseline reflects that mix. The signature is the clean way to separate constant occupancy load from weather-driven heating. Dots well above the line are days worth investigating; a baseline that drifts up over time is creep in the always-on load.',
        estate: 'Why it matters here: occupied properties draw power around the clock, so the baseline is genuinely large and mostly outside your control. The signature is the clean way to separate that occupant load from weather-driven heating. Dots well above the line are days worth investigating; a baseline that drifts up over time is creep in shared or void running costs.',
        business:
            'Why it matters here: for a site that trades long hours, evenings or weekends, the baseline reflects that trading pattern rather than round-the-clock occupancy. The signature is the clean way to separate it from weather-driven heating. Dots well above the line are days worth investigating; a baseline that drifts up over time is creep in the always-on load.',
        home: "Why it matters here: a home's baseline is smaller than a commercial site's but still runs day and night (fridges, standby devices and hot water), so the signature separates that from weather-driven heating. Dots well above the line are days worth investigating; a baseline that drifts up over time is creep in always-on appliances."
    }
}

/**
 * Resolve a term for a flavour. Missing flavour entry → school wording;
 * unknown term key → the key itself (visible enough to catch in review,
 * never an empty label).
 */
/**
 * Flavours a term deliberately does not define, with the reason.
 *
 * Every other multi-flavour term must be complete — `terminology.test.js` derives that check
 * from FLAVOURS and TERMS, so adding a sixth flavour fails for every gap at once rather than
 * silently serving the school wording. An omission listed here is a recorded decision; an
 * omission not listed here is a bug.
 *
 * This exists because `getTerm` falls back to the school term, which is the right RUNTIME
 * behaviour (an empty label is worse than a wrong one) and is exactly what hides a missing
 * entry: a business org shown school wording sees a sentence that reads as deliberate.
 *
 * Only terms that ALREADY distinguish flavours need an entry here. A term carrying a single
 * sector-neutral string ('Reduction Targets') is not a gap and needs no exemption — the check
 * fires on a term that made distinctions and then missed one, which is the shape the defect
 * actually takes.
 */
export const TERM_FLAVOUR_EXEMPTIONS = {
    // Landlord vocabulary. A university houses students under a licence rather than a
    // tenancy, and a trading business closes a site rather than voiding a let.
    tenant: {
        university: 'no tenancy relationship',
        business: 'no tenancy relationship'
    },
    tenancy: {
        university: 'no tenancy relationship',
        business: 'no tenancy relationship',
        home: 'an owner-occupier has no tenancy'
    },
    tenancies: {
        university: 'no tenancy relationship',
        business: 'no tenancy relationship',
        home: 'an owner-occupier has no tenancy'
    },
    void: {
        university: 'a hall between terms is not a void',
        home: 'a house you live in is never void'
    }
}

export function getTerm(flavour, key) {
    const entry = TERMS[key]
    if (!entry) {
        return key
    }
    return entry[flavour] ?? entry[SCHOOL_FLAVOUR]
}
